import { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, Trash2, Edit3, ShieldAlert, Star, Lightbulb, ChevronUp, ChevronDown, Users, Wrench, X, AtSign, Boxes } from 'lucide-react';
import { apiCall } from './useAutoSave';
import Dropdown from './Dropdown';

const ACCENT = '#EF9F27';

// Kompaktní stepper (číslo + šipky po straně), výška 38px jako ostatní pole
function StepperSeg({ display, onUp, onDown, onType, isDark, mutedColor, width = 52 }) {
    const box = isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)';
    const border = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const chev = { background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 0, lineHeight: 1, height: 13, display: 'flex', alignItems: 'center' };
    return (
        <div style={{ height: 38, background: box, border: `1px solid ${border}`, borderRadius: 6, display: 'flex', alignItems: 'center', padding: '0 3px 0 6px', width }}>
            <input type="text" inputMode="numeric" value={display} onChange={e => onType(parseInt(e.target.value.replace(/\D/g, '')) || 0)} onFocus={e => e.target.select()}
                style={{ flex: 1, minWidth: 0, textAlign: 'center', background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', padding: 0 }} />
            <div className="flex flex-col" style={{ gap: 2, flexShrink: 0 }}>
                <button onClick={onUp} tabIndex={-1} style={chev} onMouseEnter={e => e.currentTarget.style.color = ACCENT} onMouseLeave={e => e.currentTarget.style.color = mutedColor}><ChevronUp size={12} /></button>
                <button onClick={onDown} tabIndex={-1} style={chev} onMouseEnter={e => e.currentTarget.style.color = ACCENT} onMouseLeave={e => e.currentTarget.style.color = mutedColor}><ChevronDown size={12} /></button>
            </div>
        </div>
    );
}

// Interaktivní zadávání trvání — segmenty h/min/s se šipkami i psaním
function DurationStepper({ value, onChange, isDark, mutedColor }) {
    const total = value || 0;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const clamp = (v) => Math.max(0, Math.min(59, v));
    const commit = (nh, nm, ns) => onChange(clamp(nh) * 3600 + clamp(nm) * 60 + clamp(ns));

    return (
        <div className="flex items-center gap-1">
            <StepperSeg display={String(h).padStart(2, '0')} isDark={isDark} mutedColor={mutedColor}
                onUp={() => commit(h + 1, m, s)} onDown={() => commit(h - 1, m, s)} onType={v => commit(v, m, s)} />
            <span style={{ color: mutedColor, fontWeight: 700 }}>:</span>
            <StepperSeg display={String(m).padStart(2, '0')} isDark={isDark} mutedColor={mutedColor}
                onUp={() => commit(h, m + 1, s)} onDown={() => commit(h, m - 1, s)} onType={v => commit(h, v, s)} />
            <span style={{ color: mutedColor, fontWeight: 700 }}>:</span>
            <StepperSeg display={String(s).padStart(2, '0')} isDark={isDark} mutedColor={mutedColor}
                onUp={() => commit(h, m, s + 1)} onDown={() => commit(h, m, s - 1)} onType={v => commit(h, m, v)} />
        </div>
    );
}

const POZNAMKA_TYP_CFG = {
    bezpecnost: { label: 'Bezpecnost', Icon: ShieldAlert, color: '#EF4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.25)' },
    kvalita:    { label: 'Kvalita',    Icon: Star,        color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.25)' },
    zrucnost:   { label: 'Zrucnost',   Icon: Lightbulb,   color: '#3B82F6', bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)' },
};

// StepPostup je řízený rodičem (draft buffer): čte z `value` (sloučený krok) a
// změny hlásí přes `onPatch(patch)`. Neukládá sám — uloží se přes tlačítko Uložit.
// Poznámky jsou samostatné entity → zůstávají okamžité (apiCall + onReload).
export default function StepPostup({ value, isDark, stroje, ingredienty = [], recept, onPatch, onReload }) {
    const step = value;
    const [poznamkaForm, setPoznamkaForm] = useState(null);

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.3)' : 'var(--text-muted)';

    // Auto-výška textarey popisu podle obsahu
    const popisRef = useRef(null);
    const autoGrow = (el) => { if (!el) return; el.style.height = 'auto'; el.style.height = Math.max(80, el.scrollHeight) + 'px'; };
    useEffect(() => { autoGrow(popisRef.current); }, [step.popis, step.id]);

    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6,
        padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none',
        colorScheme: isDark ? 'dark' : 'light',
    };

    // ── @-mention ingrediencí v popisu ──────────────────────────────
    // Napíšeš „@" → nabídka ingrediencí použitých v receptu; výběr vloží „@Název".
    const [mention, setMention] = useState(null); // { query, start } | null
    const [mentionIdx, setMentionIdx] = useState(0);

    const mentionMatches = mention
        ? ingredienty.filter(i => i.nazev.toLowerCase().includes(mention.query.toLowerCase())).slice(0, 8)
        : [];

    const detectMention = (el) => {
        const pos = el.selectionStart;
        const upto = (el.value || '').slice(0, pos);
        const m = upto.match(/@([\p{L}\p{N}._-]*)$/u); // @ + jedno slovo (bez mezer) po kurzor
        if (m) { setMention({ query: m[1], start: pos - m[0].length }); setMentionIdx(0); }
        else setMention(null);
    };

    const insertMention = (ing) => {
        const el = popisRef.current;
        const full = step.popis || '';
        const pos = el ? el.selectionStart : full.length;
        const start = mention ? mention.start : pos;
        const before = full.slice(0, start);
        const after = full.slice(pos);
        const token = `@${ing.nazev} `;
        const next = before + token + after;
        onPatch({ popis: next });
        setMention(null);
        // vrátit kurzor za vložený token
        requestAnimationFrame(() => {
            if (el) { const caret = (before + token).length; el.focus(); el.setSelectionRange(caret, caret); autoGrow(el); }
        });
    };

    const onPopisKeyDown = (e) => {
        if (!mention || mentionMatches.length === 0) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx(i => (i + 1) % mentionMatches.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx(i => (i - 1 + mentionMatches.length) % mentionMatches.length); }
        else if (e.key === 'Enter') { e.preventDefault(); insertMention(mentionMatches[mentionIdx]); }
        else if (e.key === 'Escape') { setMention(null); }
    };

    // ── Poznámky (okamžité, samostatná entita) ──
    const poznList = step.poznamky || [];

    const handleSavePoznamka = async () => {
        if (!poznamkaForm?.text?.trim()) return;
        try {
            if (poznamkaForm.id) {
                await apiCall(`/api/recepty/sop/kroky/poznamky/${poznamkaForm.id}`, 'PUT', poznamkaForm);
            } else {
                await apiCall(`/api/recepty/sop/kroky/${step.id}/poznamky`, 'POST', poznamkaForm);
            }
            setPoznamkaForm(null);
            onReload();
        } catch {
            window.dispatchEvent(new CustomEvent('in-app-toast', {
                detail: { id: crypto.randomUUID(), title: 'Chyba', body: 'Nepodarilo se ulozit poznamku.' },
            }));
        }
    };

    const handleDeletePoznamka = async (pid) => {
        try {
            await apiCall(`/api/recepty/sop/kroky/poznamky/${pid}`, 'DELETE');
            onReload();
        } catch {}
    };

    // Stroje (více na krok)
    const selIds = Array.isArray(step.stroj_ids)
        ? step.stroj_ids
        : (step.stroje?.map(s => s.id) ?? (step.stroj_id ? [step.stroj_id] : []));
    const strojMap = Object.fromEntries((stroje || []).map(s => [s.id, s.nazev]));
    const setStroje = (ids) => onPatch({ stroj_ids: ids });
    const zbyva = (stroje || []).filter(s => !selIds.includes(s.id));

    return (
        <div>
            {/* Popis postupu */}
            <div className="mb-4" style={{ position: 'relative' }}>
                <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase mb-1" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>
                    Popis postupu
                    {ingredienty.length > 0 && <span className="inline-flex items-center gap-0.5" style={{ color: mutedColor, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}><AtSign size={10} /> zmiň ingredienci</span>}
                </label>
                <textarea
                    ref={popisRef}
                    value={step.popis || ''}
                    onChange={e => { onPatch({ popis: e.target.value }); detectMention(e.target); autoGrow(e.target); }}
                    onKeyDown={onPopisKeyDown}
                    onClick={e => detectMention(e.target)}
                    onBlur={() => setTimeout(() => setMention(null), 150)}
                    placeholder="Popiste postup tohoto kroku... (napiste @ pro ingredienci)"
                    rows={3}
                    style={{ ...inputStyle, minHeight: 80, resize: 'none', overflow: 'hidden' }}
                />
                {/* Nabídka @-mention */}
                {mention && mentionMatches.length > 0 && (
                    <div style={{
                        position: 'absolute', zIndex: 40, left: 8, right: 8, marginTop: 2,
                        maxHeight: 220, overflowY: 'auto', borderRadius: 7,
                        background: isDark ? '#1e1e2e' : '#fff',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}`,
                        boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
                    }}>
                        {mentionMatches.map((ing, i) => (
                            <button key={`${ing.nazev}-${i}`}
                                onMouseDown={e => { e.preventDefault(); insertMention(ing); }}
                                onMouseEnter={() => setMentionIdx(i)}
                                className="flex items-center gap-2 w-full text-left px-3 py-2"
                                style={{ background: i === mentionIdx ? (isDark ? 'rgba(239,159,39,0.16)' : 'rgba(239,159,39,0.12)') : 'transparent', border: 'none', cursor: 'pointer' }}>
                                <AtSign size={13} style={{ color: ACCENT, flexShrink: 0 }} />
                                <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{ing.nazev}</span>
                                {ing.typ && <span className="text-[9px] uppercase ml-auto" style={{ color: mutedColor }}>{ing.typ}</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Parametry kroku: čas, lidé, stroje */}
            <div className="rounded-xl p-3 mb-4 flex flex-wrap items-start gap-x-6 gap-y-3"
                style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                {/* Čas */}
                <div>
                    <label className="block text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>Čas přípravy <span style={{ color: mutedColor }}>(h:m:s)</span></label>
                    <DurationStepper value={step.cas_s || 0} onChange={v => onPatch({ cas_s: v })} isDark={isDark} mutedColor={mutedColor} />
                </div>
                {/* Lidé */}
                <div>
                    <label className="flex items-center gap-1 text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}><Users size={11} /> Lidé</label>
                    {/* `??`, ne `||` — nula je platná hodnota (u kroku běží jen
                        stroj) a `||` ji tiše přepisovalo na jedničku. */}
                    {(() => { const p = step.pocet_operatoru ?? 1; return (
                        <StepperSeg display={String(p)} width={58} isDark={isDark} mutedColor={mutedColor}
                            onUp={() => onPatch({ pocet_operatoru: Math.min(99, p + 1) })}
                            onDown={() => onPatch({ pocet_operatoru: Math.max(0, p - 1) })}
                            onType={v => onPatch({ pocet_operatoru: Math.max(0, Math.min(99, v)) })} />
                    ); })()}
                </div>
                {/* Dávka kroku. Prázdná = čas se dělí dávkou receptury a při její
                    změně se přepočítá sám. Vlastní číslo dává smysl jen tam, kde
                    jedno provedení kroku pokryje jinou porci (4 pizzy najednou). */}
                {(() => {
                    const davkaReceptu = Number(recept?.pocet_porci) || 1;
                    const jednotka = recept?.jednotka?.zkratka || '';
                    const vlastni = step.pocet_porci != null;
                    return (
                        <div>
                            <label className="flex items-center gap-1 text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>
                                <Boxes size={11} /> Krok pokryje
                            </label>
                            {vlastni ? (
                                <div className="flex items-center gap-1.5" style={{ height: 38 }}>
                                    <input type="number" min="0.01" step="any"
                                        value={step.pocet_porci}
                                        onChange={e => onPatch({ pocet_porci: e.target.value === '' ? null : Math.max(0.01, parseFloat(e.target.value) || 0.01) })}
                                        style={{
                                            height: 38, width: 78, borderRadius: 6, padding: '0 8px', fontSize: 13, textAlign: 'center',
                                            background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
                                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                                            color: 'var(--text-primary)', outline: 'none', colorScheme: isDark ? 'dark' : 'light',
                                        }} />
                                    <span className="text-[11px]" style={{ color: mutedColor }}>{jednotka}</span>
                                    <button onClick={() => onPatch({ pocet_porci: null })} title="Zpět na dávku receptury"
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, display: 'flex', padding: 2 }}>
                                        <X size={13} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2" style={{ height: 38 }}>
                                    <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                                        celou dávku <strong>{davkaReceptu} {jednotka}</strong>
                                    </span>
                                    <button onClick={() => onPatch({ pocet_porci: davkaReceptu })}
                                        title="Krok zpracuje jinou porci, než je dávka receptury (např. 4 pizzy najednou)"
                                        className="text-[11px]" style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, textDecoration: 'underline' }}>
                                        jinak
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Stroje (více na krok — sčítá se strojový čas) */}
                <div className="flex-1 min-w-[200px]">
                    <label className="flex items-center gap-1 text-[10px] font-semibold uppercase mb-1.5" style={{ color: secondaryColor, letterSpacing: '0.05em' }}><Wrench size={11} /> Stroje / nářadí</label>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {selIds.map(id => (
                            <span key={id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
                                style={{ background: isDark ? 'rgba(239,159,39,0.14)' : 'rgba(239,159,39,0.12)', color: ACCENT, border: '1px solid rgba(239,159,39,0.30)' }}>
                                <Wrench size={11} /> {strojMap[id] || `#${id}`}
                                <button onClick={() => setStroje(selIds.filter(x => x !== id))} title="Odebrat stroj"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, display: 'flex', padding: 0 }}><X size={12} /></button>
                            </span>
                        ))}
                        {zbyva.length > 0 && (
                            <div style={{ width: 176 }}>
                                <Dropdown
                                    value=""
                                    onChange={v => { const id = v ? parseInt(v) : null; if (id && !selIds.includes(id)) setStroje([...selIds, id]); }}
                                    options={[{ value: '', label: selIds.length ? '+ přidat stroj' : '— ručně —' }, ...zbyva.map(s => ({ value: s.id, label: s.nazev }))]}
                                    placeholder={selIds.length ? '+ přidat stroj' : '— ručně —'}
                                    isDark={isDark}
                                    leftIcon={<Plus size={14} style={{ color: mutedColor, flexShrink: 0 }} />}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Poznamky */}
            <div>
                <label className="block text-[10px] font-semibold uppercase mb-2" style={{ color: secondaryColor, letterSpacing: '0.05em' }}>
                    Poznamky
                </label>

                {poznList.map(p => {
                    const cfg = POZNAMKA_TYP_CFG[p.typ] || POZNAMKA_TYP_CFG.kvalita;
                    return (
                        <div key={p.id} className="flex items-start gap-2 mb-2 px-3 py-2 rounded-r-lg"
                            style={{
                                borderLeft: `2px solid ${ACCENT}`,
                                background: poznamkaForm?.id === p.id
                                    ? (isDark ? 'rgba(239,159,39,0.20)' : '#F5DFB8')
                                    : (isDark ? 'rgba(239,159,39,0.08)' : '#FAEEDA'),
                            }}>
                            <cfg.Icon size={14} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }} />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs" style={{ color: isDark ? '#e2e8f0' : '#633806' }}>{p.text}</div>
                                <div className="text-[10px] mt-0.5" style={{ color: mutedColor }}>{cfg.label}</div>
                            </div>
                            {/* Typ může být u starších záznamů prázdný — do formuláře
                                dosadíme stejný default, jaký ukazuje výpis, jinak by
                                PUT spadl na validaci. */}
                            <button onClick={() => setPoznamkaForm({
                                id: p.id,
                                text: p.text || '',
                                typ: p.typ || 'kvalita',
                                dulezitost: p.dulezitost || 'info',
                            })} title="Upravit poznámku"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2 }}>
                                <Edit3 size={12} />
                            </button>
                            <button onClick={() => handleDeletePoznamka(p.id)} title="Smazat poznámku"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2 }}>
                                <Trash2 size={12} />
                            </button>
                        </div>
                    );
                })}

                {/* Add/edit note form */}
                {poznamkaForm ? (
                    <div className="rounded-lg p-3 mb-2" style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
                        <textarea
                            value={poznamkaForm.text || ''}
                            onChange={e => setPoznamkaForm(prev => ({ ...prev, text: e.target.value }))}
                            placeholder="Text poznamky..."
                            style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginBottom: 8 }}
                            autoFocus
                        />
                        <div className="flex items-center gap-2">
                            <div style={{ width: 140 }}>
                                <Dropdown value={poznamkaForm.typ || 'kvalita'}
                                    onChange={v => setPoznamkaForm(prev => ({ ...prev, typ: v }))}
                                    options={[{ value: 'bezpecnost', label: 'Bezpečnost' }, { value: 'kvalita', label: 'Kvalita' }, { value: 'zrucnost', label: 'Zručnost' }]}
                                    isDark={isDark} height={34} />
                            </div>
                            <button onClick={handleSavePoznamka}
                                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                                style={{ background: ACCENT, color: '#412402', border: 'none', cursor: 'pointer' }}>
                                Ulozit
                            </button>
                            <button onClick={() => setPoznamkaForm(null)}
                                className="text-xs px-2 py-1.5"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: secondaryColor }}>
                                Zrusit
                            </button>
                        </div>
                    </div>
                ) : (
                    <button onClick={() => setPoznamkaForm({ text: '', typ: 'kvalita', dulezitost: 'info' })}
                        className="flex items-center gap-1 w-full py-2 rounded-lg text-xs font-medium justify-center"
                        style={{
                            background: 'none', cursor: 'pointer',
                            border: `1px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                            color: secondaryColor,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.color = ACCENT; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'; e.currentTarget.style.color = secondaryColor; }}>
                        <Plus size={13} /> pridat poznamku
                    </button>
                )}
            </div>
        </div>
    );
}
